import { HighlightStickerProps } from './types';
import { useLayoutEffect, useRef, useState } from 'react';
import TrashIcon from '../../../../assets/icons/trash.svg?react';
import { useHighlight } from '../../../../contexts/highlight-provider/useHighlight';
import { hightlightColorsAlpha } from '../../../../constants/highlights-colors-alpha';
import { cn } from '../../../../utils/common-utils';
import Markdown from '../../../markdown';
import { hightlightColors } from '../../../../constants/highlights-colors';

const HighlightSticker = ({ data }: HighlightStickerProps): JSX.Element => {
  const { updateSticker, selectedHighlightKeyId, setSelectedHighlightKeyId } =
    useHighlight();
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { color, keyId, highlightedHtml, underLine } = data;

  const getHighlightedText = (html: string, keyId: string): string => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const span = doc.querySelector(`[data-highlight-keyid="${keyId}"]`);
    return span?.innerHTML || '';
  };

  const middle = getHighlightedText(highlightedHtml, keyId);

  const processedMarkdown = `<span>${middle}</span>`;
  const description = data.sticker.description || '';

  useLayoutEffect(() => {
    const textarea = textareaRef.current;

    if (!textarea || isDeleteConfirmOpen) {
      return;
    }

    textarea.style.height = '0px';
    const nextHeight = Math.min(Math.max(textarea.scrollHeight, 54), 144);
    textarea.style.height = `${nextHeight}px`;
    setIsOverflowing(textarea.scrollHeight > 144);
  }, [description, isDeleteConfirmOpen]);

  const onChange = (description: string) => {
    updateSticker({ keyId, sticker: { description } });
  };

  const isSelected = data.keyId === selectedHighlightKeyId;

  const validatedColor = isSelected
    ? hightlightColors[color]
    : hightlightColorsAlpha[color];

  const underlineStyle = underLine
    ? {
        textDecorationLine: 'underline',
        textDecorationColor: '#000000',
        textDecorationStyle: underLine,
        textDecorationThickness: '2px',
        textUnderlineOffset: '0.18em',
        textUnderlinePosition: 'under' as const,
      }
    : undefined;

  const deleteNote = () => {
    updateSticker({
      keyId,
      sticker: { isNeedSticker: false, description: '' },
    });

    setSelectedHighlightKeyId(() => '');
    setIsDeleteConfirmOpen(false);
  };

  return isDeleteConfirmOpen ? (
    <div className="w-full overflow-hidden rounded-md border border-[#2A2A2A] bg-[#3A3A3A] text-white shadow-sm">
      <div className="flex items-center justify-between bg-[rgba(201,164,49,0.28)] px-3 py-2.5">
        <div className="text-[15px] font-semibold">Delete This Note?</div>
        <div className="flex h-8 w-8 items-center justify-center rounded-full border border-black/40 bg-[#4A4A4A]">
          <TrashIcon width={16} height={16} />
        </div>
      </div>

      <div className="flex items-center justify-center gap-5 px-3 py-3">
        <button
          type="button"
          className="min-w-[4.75rem] rounded-full border-2 border-white bg-transparent px-4 py-1 text-[13px] font-semibold underline underline-offset-4"
          onClick={(e) => {
            setIsDeleteConfirmOpen(false);
            e.stopPropagation();
          }}
        >
          No
        </button>

        <button
          type="button"
          className="text-[13px] font-semibold hover:underline hover:underline-offset-4"
          onClick={(e) => {
            deleteNote();
            e.stopPropagation();
          }}
        >
          Yes
        </button>
      </div>
    </div>
  ) : (
    <div
      className={cn(
        'relative cursor-pointer',
        isSelected
          ? 'w-[92%] max-w-[19rem] self-start'
          : 'w-[92%] max-w-[19rem] self-center',
      )}
      onClick={() => {
        if (!isSelected) {
          setSelectedHighlightKeyId(keyId);
        }
      }}
    >
      <div className="flex w-full flex-col overflow-hidden rounded-md border border-gray bg-white">
        <div
          className="pointer-events-none flex h-max w-full select-none justify-between gap-2 p-2"
          style={{ backgroundColor: validatedColor }}
        >
          <div
            className={cn(
              'max-w-[80%] overflow-hidden truncate overflow-ellipsis text-nowrap text-left text-sm font-semibold',
            )}
            style={underlineStyle}
          >
            <Markdown>{processedMarkdown}</Markdown>
          </div>
          <div
            className={
              'pointer-events-auto flex h-6 w-6 cursor-pointer select-auto items-center justify-center rounded-full border border-black border-opacity-75 bg-white hover:border-2'
            }
            onClick={(e) => {
              setIsDeleteConfirmOpen(true);
              e.stopPropagation();
            }}
          >
            <TrashIcon width={18} height={18} />
          </div>
        </div>

        <textarea
          ref={textareaRef}
          rows={1}
          className={cn(
            'min-h-[54px] max-h-36 w-full resize-none rounded-b-md border-t border-gray p-2 text-sm leading-5 outline-none',
            isOverflowing ? 'overflow-y-auto' : 'overflow-y-hidden',
          )}
          placeholder="Notes are saved automatically."
          value={description}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
    </div>
  );
};

export default HighlightSticker;
